import { useEffect, useState } from 'react'
import useUserRole from 'src/hooks/useUserRole'
import { useVenueCustomRoles } from 'src/hooks/useIsBlocked'
import { canAccess } from 'src/utils/permissions'
import { fetchPendingApprovalsCount } from 'src/utils/dashboardApprovalsApi'
import { notifyVenueHomeChanged } from 'src/utils/venueHomeApi'

const POLL_INTERVAL_MS = 20_000

/**
 * Fired by the Approvals screen whenever its list (re)loads or a decision is
 * made, so open tabs update their badge immediately instead of waiting a poll.
 */
export const APPROVALS_CHANGED_EVENT = 'oms:approvals-changed'

export const notifyApprovalsChanged = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(APPROVALS_CHANGED_EVENT))
  notifyVenueHomeChanged()
}

/**
 * Outstanding pending-approval count for the venue nav badge.
 * Only polls when the signed-in user can actually open the Approvals area,
 * so unauthorised users never trigger (or see the result of) the endpoint.
 * Updates every 20s, plus immediately on tab focus and after any approval
 * decision anywhere in the app (APPROVALS_CHANGED_EVENT). Failures stay
 * silent — a badge must never break the dashboard chrome.
 */
const usePendingApprovalsCount = venueId => {
  const [count, setCount] = useState(0)
  const role = useUserRole()
  const customRoles = useVenueCustomRoles()
  const canSeeApprovals = canAccess('approvals', role, customRoles)

  useEffect(() => {
    if (!venueId || !canSeeApprovals) {
      setCount(0)
      return undefined
    }

    let cancelled = false

    const load = () => {
      fetchPendingApprovalsCount({ venueId })
        .then(next => {
          if (!cancelled) {
            setCount(next)
          }
          return null
        })
        .catch(error => {
          console.error('usePendingApprovalsCount poll failed', error)
          return null
        })
    }

    load()
    const interval = setInterval(load, POLL_INTERVAL_MS)

    const onFocus = () => load()
    window.addEventListener('focus', onFocus)
    window.addEventListener(APPROVALS_CHANGED_EVENT, onFocus)

    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener(APPROVALS_CHANGED_EVENT, onFocus)
    }
  }, [venueId, canSeeApprovals])

  return count
}

export default usePendingApprovalsCount
